import React from "react";
import { cn } from "@/utils/cn";
import { CalendarCheckIcon } from "@/components/icons";
import { formatScheduledLabel } from "./formatScheduledLabel";

interface CarouselIndicatorProps {
  currentIndex: number;
  totalItems: number;
}

export const CarouselIndicator: React.FC<CarouselIndicatorProps> = ({
  currentIndex,
  totalItems,
}) => (
  <div className="flex gap-1">
    {Array.from({ length: totalItems }, (_, index) => (
      <div
        key={index}
        className={cn(
          "w-2 h-2 rounded-full",
          index === currentIndex ? "bg-white" : "bg-white/50"
        )}
      />
    ))}
  </div>
);

interface ReelBadgeProps {}

export const ReelBadge: React.FC<ReelBadgeProps> = () => (
  <div className="bg-black/70 p-1.5 rounded-sm">
    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
      <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
    </svg>
  </div>
);

interface CarouselCountBadgeProps {
  currentIndex: number;
  totalItems: number;
}

export const CarouselCountBadge: React.FC<CarouselCountBadgeProps> = ({
  currentIndex,
  totalItems,
}) => (
  <div className="min-w-[20px] h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold px-1">
    {currentIndex + 1}/{totalItems}
  </div>
);

interface ScheduledBadgeProps {
  scheduledAt?: string | null;
  variant?: "scheduled" | "published";
}

export const ScheduledBadge: React.FC<ScheduledBadgeProps> = ({ scheduledAt, variant = "scheduled" }) => {
  const isPublished = variant === "published";
  const label = formatScheduledLabel(scheduledAt, variant);
  const bgColor = isPublished ? "bg-green-600/90" : "bg-black/70";

  return (
    <div className="relative group/scheduled">
      <div
        className={cn(
          bgColor,
          "rounded-sm text-white flex items-center",
          scheduledAt ? "gap-1.5 px-2 py-1" : "p-1.5"
        )}
      >
        <CalendarCheckIcon className="w-4 h-4 shrink-0" />
        {scheduledAt && (
          <span className="text-xs font-medium whitespace-nowrap">{label}</span>
        )}
      </div>
      {scheduledAt && (
        <div className="absolute left-full bottom-0 ml-1.5 px-2 py-1 bg-gray-900 text-white text-xs rounded-sm whitespace-nowrap opacity-0 pointer-events-none group-hover/scheduled:opacity-100 transition-opacity duration-150 z-30">
          {label}
        </div>
      )}
    </div>
  );
};

export const AIGeneratedBadge: React.FC = () => (
  <div className="bg-violet-500/90 px-2 py-1 rounded-md flex items-center gap-1.5 text-white text-xs font-medium shadow-xs">
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
    <span>AI Generated</span>
  </div>
);
